import { HIT_POINTS } from '../../character/content/templates';

const statBenchmarks = [
  { stat: 'Attack Modifier', t1: '+1', t2: '+2', t3: '+3', t4: '+4' },
  { stat: 'Damage Dice', t1: '1d6+2 to 1d12+4', t2: '2d6+3 to 2d12+4', t3: '3d8+3 to 3d12+5', t4: '4d8+10 to 4d12+15' },
  { stat: 'Difficulty', t1: '11', t2: '14', t3: '17', t4: '20' },
  { stat: 'Damage Thresholds', t1: 'Major 7/Severe 12', t2: 'Major 10/Severe 20', t3: 'Major 20/Severe 32', t4: 'Major 25/Severe 45' },
];

export default function AdversaryGuideContent() {
  return (
    <>
      <h3 id="adversary-roles">ADVERSARY ROLES</h3>
      <ul>
        {[
          { role: 'Bruiser', desc: 'Tough; deliver powerful attacks.' },
          { role: 'Hordes', desc: 'Groups of identical creatures acting together as a single unit.' },
          { role: 'Leader', desc: 'Command and summon other adversaries.' },
          { role: 'Minions', desc: 'Easily dispatched but dangerous in numbers.' },
          { role: 'Skulks', desc: 'Maneuver and exploit opportunities to ambush opponents.' },
          { role: 'Socials', desc: 'Present challenges around conversation instead of combat.' },
          { role: 'Solos', desc: 'Present a formidable challenge to a whole party, with or without support.' },
          { role: 'Standards', desc: 'Representative of their fictional group.' },
          { role: 'Supports', desc: 'Enhance their allies and disrupt their opponents.' },
        ].map((r) => (
          <li key={r.role}><strong>{r.role}</strong> — {r.desc}</li>
        ))}
      </ul>

      <h3 id="adversary-anatomy">ADVERSARY ANATOMY</h3>
      <ul>
        {[
          { label: 'Name & Role', text: 'Identifies the adversary type and combat role.' },
          { label: 'Tier', text: 'Indicates power level (Tier 1–4, matching PC tiers).' },
          { label: 'HP & Armor', text: 'Hit Points and armor score for the adversary.' },
          { label: 'Attack', text: 'Name, range, and damage of their primary attack.' },
          { label: 'Difficulty', text: 'The target number players must meet on action rolls against them.' },
          { label: 'Features', text: 'Special abilities, passive traits, and Fear-powered moves.' },
        ].map(({ label }) => (
          <li key={label}><strong>{label}</strong> — {
            label === 'Name & Role' ? 'Identifies the adversary type and combat role.' :
              label === 'Tier' ? 'Indicates power level (Tier 1–4, matching PC tiers).' :
                label === 'HP & Armor' ? <>{HIT_POINTS} and armor score for the adversary.</> :
                  label === 'Attack' ? 'Name, range, and damage of their primary attack.' :
                    label === 'Difficulty' ? 'The target number players must meet on action rolls against them.' :
                      'Special abilities, passive traits, and Fear-powered moves.'
          }</li>
        ))}
      </ul>

      <h3 id="adversary-features">EXAMPLE ADVERSARY FEATURES</h3>

      <h4 id="actions">ACTIONS</h4>
      <div className="feature-block-container">
        {[
          { name: 'Haymaker', text: 'Make an attack against a target within Very Close range. On a success, deal X direct physical damage and the target must mark an Armor Slot without gaining its benefit (they can still use armor to reduce the damage).' },
          { name: 'Shredding Strike', text: 'Make an attack against a target within Very Close range. On a success, deal X physical damage and the target must mark an Armor Slot without gaining its benefit (they can still use armor to reduce the damage).' },
          { name: 'More Where That Came From', text: 'Summon three Jagged Knife Lackeys, who appear at Far range.' },
        ].map((f) => (

          <div key={f.name} className="feature-block">
            <div className="feature-name">{f.name} <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>— Action</span></div>
            <div className="feature-text">{f.text}</div>
          </div>
        ))}
      </div>
      <h4 id="reactions">REACTIONS</h4>
      <div className="feature-block-container">
        {[
          { name: 'Heavy Hitter', text: 'When this adversary deals damage with a standard attack, you can spend a Fear to gain a +X bonus to the damage roll.' },
          { name: 'Team-Up', text: 'When another adversary within Very Close range of this adversary deals X damage to a creature, you can mark a Stress to make a standard attack against that same creature. On a success, combine the damage.' },
          { name: 'Momentum', text: 'When this adversary makes a successful attack against a PC, you gain a Fear.' },
        ].map((f) => (
          <div key={f.name} className="feature-block">
            <div className="feature-name">{f.name} <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>— Reaction</span></div>
            <div className="feature-text">{f.text}</div>
          </div>
        ))}
      </div>
      <h4 id="passives">PASSIVES</h4>
      <div className="feature-block-container">
        {[
          { name: 'Horde (X)', text: 'When the Horde has marked half or more of their HP, their standard attack deals X damage instead.' },
          { name: 'Minion (X)', text: 'This adversary is defeated when they take any damage. For every X damage a PC deals to this adversary, defeat an additional Minion within range the attack would succeed against.' },
          { name: 'Relentless (X)', text: 'This adversary can be spotlighted up to X times per GM turn. Spend Fear as usual to spotlight them.' },
          { name: 'Slow', text: "When you spotlight this adversary and they don't have a token on their stat block, they can't act yet. Place a token on their stat block and describe what they're preparing to do. When you spotlight this adversary and they have a token on their stat block, clear the token and they can act." },
          { name: 'Arcane Form', text: 'This adversary is resistant to magic damage.' },
          { name: 'Armored Carapace', text: 'When this adversary takes physical damage, reduce it by X.' },
        ].map((f) => (
          <div key={f.name} className="feature-block">
            <div className="feature-name">{f.name} <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>— Passive</span></div>
            <div className="feature-text">{f.text}</div>
          </div>
        ))}
      </div>

      <h4 id="fear-features">FEAR FEATURES</h4>
      <div className="feature-block-container">
        <div className="feature-block">
          <div className="feature-name">Explosion <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>— Action</span></div>
          <div className="feature-text">Spend a Fear to erupt in a fiery explosion. Make an attack against all targets within Close range. Targets the adversary succeeds against take 1d8 magic damage and are knocked back to Far range.</div>
        </div>
      </div>
      <h3 id="building-encounters">BUILDING BALANCED ENCOUNTERS</h3>
      <p>When planning a battle, start with <strong>[(3 x the number of PCs in combat) + 2] Battle Points</strong> and make the following adjustments:</p>
      <ul>
        <li><strong>-1</strong> for an easier or shorter fight</li>
        <li><strong>-2</strong> if you're using 2 or more Solo adversaries</li>
        <li><strong>-2</strong> if you add +1d4 (or a static +2) to all adversaries' damage rolls</li>
        <li><strong>+1</strong> if you choose an adversary from a lower tier</li>
        <li><strong>+1</strong> if you don't include any Bruisers, Hordes, Leaders, or Solos</li>
        <li><strong>+2</strong> for a harder or longer fight</li>
      </ul>
      <p>Then spend your Battle Points to add an adversary to the encounter:</p>
      <ul>
        <li><strong>Spend 1 point</strong> for each group of Minions equal to the size of the party.</li>
        <li><strong>Spend 1 point</strong> for each Social or Support adversary.</li>
        <li><strong>Spend 2 points</strong> for each Horde, Ranged, Skulk, or Standard adversary.</li>
        <li><strong>Spend 3 points</strong> for each Leader adversary.</li>
        <li><strong>Spend 4 points</strong> for each Bruiser adversary.</li>
        <li><strong>Spend 5 points</strong> for each Solo adversary.</li>
      </ul>

      <h3 id="defeated-adversaries">DEFEATED ADVERSARIES</h3>
      <p>When an adversary marks their last {HIT_POINTS}, they are defeated: incapacitated, tied up, routed, killed, or anything else the table decides makes sense.</p>

      <h3 id="stat-block-benchmarks">ADVERSARY STAT BLOCK BENCHMARKS</h3>
      <div style={{ overflowX: 'auto' }}>
        <table className="equipment-table">
          <thead>
            <tr>
              <th>Adversary Statistic</th>
              <th>Tier 1</th>
              <th>Tier 2</th>
              <th>Tier 3</th>
              <th>Tier 4</th>
            </tr>
          </thead>
          <tbody>
            {statBenchmarks.map((r) => (
              <tr key={r.stat}>
                <td><strong>{r.stat}</strong></td>
                <td>{r.t1}</td>
                <td>{r.t2}</td>
                <td>{r.t3}</td>
                <td>{r.t4}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
